import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider, responsiveFontSizes } from '@mui/material/styles';
import {
    FileText,
    Undo2,
    Wallet,
    TrendingUp,
    Users,
    Package,
    BarChart3,
    Mail,
    FileSpreadsheet,
} from 'lucide-react';

// Assuming these are your local components
import Nav from '../new pages/Nav';
import Footer from '../footer';
import Scrolltotop from '../../Button/Button';

// A responsive theme for better typography
let theme = createTheme({
    palette: {
        primary: { main: '#1976d2' },
        secondary: { main: '#dc004e' },
        background: { default: '#ffffff', paper: '#f4f6f8' },
        text: {
            primary: '#000000',
            secondary: '#333333'
        }
    },
    typography: {
        fontFamily: 'Inter, sans-serif',
        h3: { fontWeight: 700, color: 'black' },
        h4: { fontWeight: 800, color: 'black' }, // Added for feature titles
        h6: { color: '#333' },
        body1: { fontSize: '1.1rem', lineHeight: 1.6 }
    },
});
theme = responsiveFontSizes(theme);

// --- Data for Features ---
const allFeatures = [
    {
        icon: <FileText />,
        title: 'Unlimited Invoicing',
        description: 'Create and send professional invoices to your clients without any limits.',
        color: '#29b6f6', // Light Blue
    },
    {
        icon: <Undo2 />,
        title: 'Manage Returns Effortlessly',
        description: 'Efficiently handle sales returns and credit notes to maintain accurate records.',
        color: '#ffa726', // Orange
    },
    {
        icon: <Wallet />,
        title: 'Comprehensive Expense Tracking',
        description: 'Record, categorize, and monitor all your business expenses in one place.',
        color: '#66bb6a', // Green
    },
    {
        icon: <TrendingUp />,
        title: 'Investment Management',
        description: 'Keep track of your business investments and their performance over time.',
        color: '#ef5350', // Red
    },
    {
        icon: <Users />,
        title: 'Customer & Supplier Hub',
        description: 'Manage your contacts, track their history, and build strong relationships.',
        color: '#ab47bc', // Purple
    },
    {
        icon: <Package />,
        title: 'Product & Material Management',
        description: 'Organize your inventory, manage stock levels, and track product details.',
        color: '#78909c', // Blue Grey
    },
    {
        icon: <BarChart3 />,
        title: 'Insightful Financial Reports',
        description: 'Access a suite of reports like P&L, sales summaries, and outstanding balances.',
        color: '#5c6bc0', // Indigo
    },
    {
        icon: <Mail />,
        title: 'Direct Email Communication',
        description: 'Send invoices and statements directly to your customers via email.',
        color: '#42a5f5', // Blue
    },
    {
        icon: <FileSpreadsheet />,
        title: 'Export Data to Excel',
        description: 'Export your financial reports to Excel for further analysis or sharing.',
        color: '#26a69a', // Teal
    },
];

// --- Feature Row Component (Styled like ReportHeroSection) ---
const FeatureRow = ({ feature, index }) => {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isReversed = index % 2 !== 0;

    return (
        <Box sx={{ width: '100%', py: { xs: 6, md: 1 } }}>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    // Alternates layout direction based on index
                    flexDirection: isMobile ? 'column' : (isReversed ? 'row-reverse' : 'row'),
                    alignItems: 'center',
                    gap: { xs: 6, md: 10 },
                }}
            >
                {/* Text Section */}
                <Box sx={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
                    <Typography
                        variant="h4"
                        sx={{
                            color: '#0F172A',
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            mb: 3,
                            letterSpacing: '-0.02em'
                        }}
                    >
                        {feature.title}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#475569',
                            fontSize: '1.125rem',
                            lineHeight: 1.7,
                            maxWidth: '500px',
                            mx: isMobile ? 'auto' : 0
                        }}
                    >
                        {feature.description}
                    </Typography>
                </Box>

                {/* Mock UI Section (Visual Box mimicking Hero style) */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    width: '100%',
                    height: { xs: '300px', md: '400px' }
                }}>
                    
                    {/* Abstract Background Blob matched to feature color */}
                    <Box
                        sx={{
                            position: 'absolute',
                            height: { xs: '220px', sm: '300px' },
                            width: { xs: '220px', sm: '300px' },
                            background: `linear-gradient(135deg, ${feature.color} 0%, #E0E7FF 100%)`, 
                            borderRadius: '50% 50% 30% 70% / 70% 30% 70% 30%', 
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(15deg)',
                            zIndex: 0,
                            opacity: 0.25,
                            filter: 'blur(20px)'
                        }}
                    />

                    {/* Main Feature Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            maxWidth: '280px',
                            backgroundColor: '#ffffff',
                            borderRadius: '24px',
                            p: 4,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2
                        }}
                    >
                        <Box sx={{ 
                            width: 80, 
                            height: 80, 
                            borderRadius: '20px',
                            backgroundColor: `${feature.color}15`, // Light transparent version of feature color
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: feature.color,
                        }}>
                            {/* Clones the Lucide icon to make it larger inside the card */}
                            {React.cloneElement(feature.icon, { size: 40 })}
                        </Box>
                        <Typography sx={{ fontSize: '1.1rem', color: '#0F172A', fontWeight: 700, textAlign: 'center' }}>
                            {feature.title}
                        </Typography>
                    </Paper>

                </Box>
            </Box>
        </Box>
    );
};

// --- Main Features Component ---
export default function Features() {
    return (
        <>
            <ThemeProvider theme={theme}>
                <Nav />
                <Box sx={{ py: 8, zIndex: 1, position: 'relative' }}>
                    <Container maxWidth="lg">
                        {/* --- Title --- */}
                        <Box sx={{ textAlign: 'center', mb: 10, mt: 8 }}>
                            <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom mt={7} color={'black'}>
                                Everything You Need, All in One Place
                            </Typography>
                            <Typography variant="body1" textAlign="center" mb={5} sx={{ color: 'rgba(95, 95, 95, 0.9)', maxWidth: '700px', mx: 'auto' }}>
                                Our accounting system is packed with powerful tools to help you manage your finances with ease and precision.
                            </Typography>
                        </Box>

                        {/* --- Features List --- */}
                        <Box sx={{ backgroundColor: 'white', borderRadius: '32px', p: { xs: 3, md: 6 }, boxShadow: '0 10px 40px rgba(0,0,0,0.05)' }}>
                            {allFeatures.map((feature, index) => (
                                <React.Fragment key={index}>
                                    <FeatureRow feature={feature} index={index} />
                                    {index < allFeatures.length - 1 && (
                                        <Box sx={{ height: '1px', bgcolor: 'rgba(226, 232, 240, 0.8)', my: 2 }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Container>
                </Box>
                {/* Background Gradient */}
                <Box sx={{ width: '100%', background: 'linear-gradient(to bottom, #F8FAFC, #ffffff)', minHeight: '80vh', height: '100vh', zIndex: -1, position: 'absolute', top: 0 }}></Box>
            </ThemeProvider>
            <Footer />
            <Scrolltotop />
        </>
    );
}